import React, { useEffect, useMemo } from 'react';
import { FormHelperText, Grid } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import {
  ReactFormInput,
  ReactFormSelect,
  SceneSelect,
  UserSelect,
} from '../../../../components';
import { OrderStatuses } from '../../../../constants/orders';
import Input from '../../../../components/form-input/Input';
import StepModalCaption from '../../../../components/modal/StepModalCaption/StepModalCaption';
import ReactFormCheckbox from '../../../../components/form-checkbox/ReactFormCheckbox';
import ReactFormRadioGroup from '../../../../components/form-radio-group/ReactFormRadioGroup';
import { closedCaptions, durationOptions, firstEditOptions } from '../../modals/VideoEditStepModal/constants';
import { useGuard } from '../../../../hooks';
import { is, roles } from '../../../../utility/guard';
import { selectProject } from '../../../../store/modules/projects/selectors';
import useStyles from '../styles';
import useOrderSameDay from '../../../../hooks/useOrderSameDay';
import VideoEditOrderCheckForm from '../../../../components/video-edit-order-check/VideoEditOrderCheckForm';

const ProjectOrderFormFields = () => {
  const classes = useStyles();
  const isSuperAdmin = useGuard(is(roles.superAdmin));
  const { data: project } = useSelector(selectProject);
  const { watch, setValue, errors } = useFormContext();
  const checkboxStandard = watch('options.format.standard');
  const checkboxSquare = watch('options.format.square');
  const checkboxPortrait = watch('options.format.portrait');
  const videoDuration = watch('options.video_duration_key');
  const assignedToId = watch('assigned_to_id');
  const isAllowedSameDay = useOrderSameDay();
  const userUnassignedOption = useMemo(
    () => ((!isSuperAdmin && assignedToId < 0) ? { id: -1, label: 'Unassigned' } : undefined),
    [isSuperAdmin, assignedToId],
  );

  useEffect(() => {
    setValue('options.format.standard', checkboxStandard, { shouldValidate: true });
  }, [checkboxSquare, checkboxPortrait, checkboxStandard, setValue]);

  return (
    <Grid container spacing={4}>

      <Grid item xs={12} sm={6}>
        <ReactFormSelect
          name="status_id"
          label="Order Status"
          items={OrderStatuses.map((opt) => ({ id: opt.key, title: opt.label }))}
          disabled={!isSuperAdmin}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <UserSelect
          name="assigned_to_id"
          label="Assigned to"
          disabled={!isSuperAdmin}
          filters={[{ key: 'is_super_admin', value: 1 }]}
          formType="react"
          setOption={userUnassignedOption}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <SceneSelect
          name="scene_id"
          label="Script"
          filters={[{ key: 'project_id', value: project!.id }]}
          isRequired
          formType="react"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Input label="Project" value={project!.title} onChange={(e) => {}} disabled />
      </Grid>

      <Grid item xs={12} sm={6}>

        <ReactFormInput
          name="options.order_title"
          label="Order Title (optional)"
        />

        <StepModalCaption
          title="Format"
          tooltip="Select the video formats that you want to use for different web site and social media services"
        />

        <ReactFormCheckbox
          label="Standard: YouTube / Facebook / Vimeo / LinkedIn"
          name="options.format.standard"
        />
        <ReactFormCheckbox
          label="Square: Instagram"
          name="options.format.square"
        />
        <ReactFormCheckbox
          label="Portrait: Instagram Story, IGTV, Facebook Story"
          name="options.format.portrait"
        />

        {errors?.options?.format?.standard && (
          <FormHelperText id="Format" error>{errors.options.format.standard.message}</FormHelperText>
        )}

        <StepModalCaption
          title="Closed Captions / Subtitles in U.S. English"
          tooltip="Subtitles can be included in the video file (most common method) or in a separate file."
        />

        <ReactFormRadioGroup name="options.closed_captions" options={closedCaptions} />

        <StepModalCaption
          title="Video Duration"
          tooltip="Video Duration"
        />

        <div className={classes.inputWrapper}>
          <ReactFormSelect
            items={durationOptions}
            name="options.video_duration_key"
            label="Desired Duration"
            tooltip="What range of duration do you think the video will be in?"
          />
        </div>
        { videoDuration === 3 && (
          <div className={classes.inputWrapper}>
            <ReactFormInput
              name="options.additional_min"
              label="How many minutes?"
              endAdornment="minutes"
              type="number"
              tooltip="How many total minutes do you expect the video to be?"
              inputProps={{ min: 6 }}
            />
          </div>
        )}
        <Grid item xs={12}>
          <StepModalCaption
            title="First Edit"
            tooltip="The First Edit is the first video edit effort.
                       There might be other changes which need to be made after the First Edit & before
                       he video is ready to publish."
          />
          <ReactFormRadioGroup
            name="first_edit_delivery_id"
            options={firstEditOptions}
            blockedIds={isAllowedSameDay ? [] : ['same_day']}
          />
        </Grid>
      </Grid>

      <Grid item xs={12} sm={6}>
        <VideoEditOrderCheckForm />
      </Grid>
    </Grid>
  );
};

export default ProjectOrderFormFields;
